import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import Header from "@/components/Header";
import ApplicantCard from "@/components/ApplicantCard";
import FilterSheet from "@/components/FilterSheet";
import PostingSummary from "@/components/PostingSummary";
import { fetchMatches, requestContact } from "@/lib/api";
import { FilterOptions } from "@/lib/types";

export default function PostingPage() {
  const params = useParams();
  const postingId = params.postingId as string;

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Partial<FilterOptions>>({});
  const [page, setPage] = useState(1);
  const [requestedApplicants, setRequestedApplicants] = useState<Set<string>>(
    new Set()
  );

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ["matches", postingId, page, filters],
    queryFn: () =>
      fetchMatches({
        postingId,
        page,
        limit: 20,
        filters,
      }),
    enabled: !!postingId,
  });

  const contactMutation = useMutation({
    mutationFn: (applicantId: string) =>
      requestContact(postingId, applicantId),
    onSuccess: (_, applicantId) => {
      setRequestedApplicants((prev) => new Set([...prev, applicantId]));
    },
  });

  const handleApplyFilters = (newFilters: Partial<FilterOptions>) => {
    setFilters(newFilters);
    setPage(1);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    setFilters({});
    setPage(1);
    setShowFilters(false);
  };

  if (!postingId || isLoading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-lg md:max-w-3xl lg:max-w-6xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow p-6 text-center lg:max-w-md">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-lg md:max-w-3xl lg:max-w-6xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow p-6 text-center lg:max-w-md">
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Unable to Load
            </h1>
            <p className="text-gray-600">
              {error instanceof Error ? error.message : "Something went wrong"}
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { posting, matches, pagination } = data;
  const hasActiveFilters =
    (filters.subjects && filters.subjects.length > 0) ||
    (filters.levels && filters.levels.length > 0) ||
    (filters.scheme && filters.scheme.length > 0) ||
    filters.minExperience;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-lg md:max-w-3xl lg:max-w-6xl mx-auto px-4 py-6">
        <div className="lg:flex lg:gap-8">
          {/* Posting Summary - Sticky sidebar on desktop */}
          <div className="lg:w-80 lg:flex-shrink-0">
            <div className="bg-white rounded-lg shadow p-4 mb-4 lg:sticky lg:top-6">
              <PostingSummary posting={posting} totalMatches={pagination.total} />

              {/* Filter Button */}
              <button
                onClick={() => setShowFilters(true)}
                className={`mt-4 w-full py-2.5 px-4 rounded-full border flex items-center justify-center gap-2 transition-colors ${
                  hasActiveFilters
                    ? "border-primary-500 bg-primary-50 text-primary-600"
                    : "border-gray-300 hover:border-gray-400 text-gray-700"
                }`}
              >
                <FilterIcon />
                <span>Filter{hasActiveFilters ? " (Active)" : ""}</span>
              </button>
            </div>
          </div>

          {/* Applicant Cards Section */}
          <div className="lg:flex-1 lg:min-w-0">
            {/* Applicant Cards */}
            {matches.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-6 text-center text-gray-600">
                No matching applicants found.
                {hasActiveFilters && (
                  <button
                    onClick={handleResetFilters}
                    className="block mx-auto mt-2 text-primary-600 hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {matches.map((applicant) => (
                  <ApplicantCard
                    key={applicant.id}
                    applicant={applicant}
                    onRequestContact={() => contactMutation.mutate(applicant.id)}
                    isRequesting={
                      contactMutation.isPending &&
                      contactMutation.variables === applicant.id
                    }
                    isRequested={requestedApplicants.has(applicant.id)}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination.total > 0 && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-600">
                <span>
                  Showing {(page - 1) * pagination.limit + 1}-
                  {Math.min(page * pagination.limit, pagination.total)} of{" "}
                  {pagination.total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.hasMore}
                    className="px-4 py-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Filter Sheet */}
      {showFilters && (
        <FilterSheet
          posting={posting}
          currentFilters={filters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          onClose={() => setShowFilters(false)}
        />
      )}
    </div>
  );
}

function FilterIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 4H14M4 8H12M6 12H10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
